import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useActivityTracker } from '../hooks/useActivityTracker';
import { api } from '../services/api';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  ChevronDown,
  ChevronRight,
  Archive,
  Bell,
  MessageSquare,
  Smartphone,
  Target,
  Shield,
  TrendingUp,
  Globe,
} from 'lucide-react';
import { useBankerLeads, useMarkLeadActedOn, useBankerNotifications } from '../hooks/useBankerLeads';
import { useWatchlistSignals } from '../hooks/useWatchlistSignals';
import type { BankerLead, NotificationHistoryItem } from '../types/expert';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { useAuth } from '../contexts/AuthContext';
import { GitMerge } from 'lucide-react';
import {
  extractExchange,
  formatRelativeTime,
  groupLeadsByExchangeThenSymbol,
  latestLeadIdBySymbol,
  sortLeadsNewestFirst,
  type ExchangeGroup,
  type SymbolGroup,
} from '../utils/bankerLeadsOrdering';

type Tab = 'position_alerts' | 'opportunities' | 'archived' | 'notifications';
type DateRange = 'today' | '7d' | '30d' | 'all';
type PriorityFilter = '' | 'high' | 'medium' | 'low';
type LeadView = 'latest' | 'exchange';

function formatAbsoluteDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? 'Analysis time unavailable' : date.toLocaleString();
}

function AnalysisTimestamp({ date }: { date: string | null | undefined }) {
  if (!date) return <span>Analysis time unavailable</span>;
  return (
    <span title={formatAbsoluteDateTime(date)}>
      Analysed {formatRelativeTime(date)}
    </span>
  );
}

function LatestBadge({ latest }: { latest: boolean }) {
  return latest ? (
    <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300 font-medium">Latest</span>
  ) : (
    <span className="px-2 py-0.5 text-xs rounded-full bg-slate-500/20 text-slate-400">Superseded</span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const BankerLeads: React.FC = () => {
  const { user } = useAuth();
  const { trackEvent } = useActivityTracker();
  const [activeTab, setActiveTab] = useState<Tab>('position_alerts');
  // 3-level expand state: exchange section → symbol group → individual lead detail
  const [expandedExchange, setExpandedExchange] = useState<string | null>(null);
  const [expandedSymbol, setExpandedSymbol] = useState<string | null>(null);
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null);
  // Opportunity Leads sub-filter: show approved/acted_on or rejected
  const [showRejected, setShowRejected] = useState(false);
  const [leadView, setLeadView] = useState<LeadView>(() => {
    try {
      return localStorage.getItem('banker-leads-view') === 'exchange' ? 'exchange' : 'latest';
    } catch {
      return 'latest';
    }
  });
  const changeLeadView = (view: LeadView) => {
    setLeadView(view);
    try {
      localStorage.setItem('banker-leads-view', view);
    } catch {
      // Rendering remains usable when storage is unavailable.
    }
  };
  const renderLeadViewToggle = () => (
    <div className="flex bg-slate-800 rounded-lg p-0.5" aria-label="Lead view">
      {(['latest', 'exchange'] as LeadView[]).map((view) => (
        <button
          key={view}
          onClick={() => changeLeadView(view)}
          aria-pressed={leadView === view}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${leadView === view ? 'bg-slate-700 text-teal-400' : 'text-slate-400 hover:text-slate-200'}`}
        >
          {view === 'latest' ? 'Latest' : 'By exchange'}
        </button>
      ))}
    </div>
  );
  // Notification filters
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('');

  // Watchlist signals — used to show "Converged" badge on leads that have matching signals
  const { data: signalsData } = useWatchlistSignals({ limit: 200 });
  // Set of banker_lead_ids that have a watchlist signal convergence
  const convergedLeadIds = new Set(
    (signalsData?.items ?? [])
      .filter((s) => s.convergence && s.banker_lead_id != null)
      .map((s) => s.banker_lead_id as number)
  );

  // Separate data channels per tab
  const { data: alertData, isLoading: alertLoading, error: alertError } = useBankerLeads({
    lead_type: 'position_alert',
    status: 'approved,acted_on',
    limit: 100,
  });
  const { data: oppData, isLoading: oppLoading, error: oppError } = useBankerLeads({
    lead_type: 'opportunity',
    status: showRejected ? 'rejected' : 'approved,acted_on',
    limit: 100,
  });
  const { data: archivedOppData, isLoading: archivedOppLoading, error: archivedOppError } = useBankerLeads({
    status: 'archived',
    lead_type: 'opportunity',
    limit: 100,
  });
  const { data: archivedAlertData, isLoading: archivedAlertLoading, error: archivedAlertError } = useBankerLeads({
    status: 'archived',
    lead_type: 'position_alert',
    limit: 100,
  });
  const markActed = useMarkLeadActedOn();

  // Signal action policy — hide "Mark as Acted On" for notify_only orgs
  // Platform admins (is_admin=true) have no org — skip the fetch entirely
  const { data: policyData } = useQuery({
    queryKey: ['signal-policy'],
    queryFn: () => api.request<{ org_id: number; signal_action_policy: string }>('/api/v1/orgs/signal-policy'),
    enabled: !user?.is_admin,
    retry: false,
    throwOnError: false,
  });
  const signalPolicy = policyData?.signal_action_policy ?? 'open';
  const canActOnSignals = signalPolicy !== 'notify_only';

  const {
    data: notifData,
    isLoading: notifLoading,
    error: notifError,
  } = useBankerNotifications({
    date_range: dateRange,
    priority: priorityFilter || undefined,
    limit: 100,
  });

  const alertLeads = alertData?.items || [];
  const oppLeads = oppData?.items || [];
  const archivedOppLeads = archivedOppData?.items || [];
  const archivedAlertLeads = archivedAlertData?.items || [];
  const notifications = notifData?.items || [];

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'position_alerts', label: 'Position Alerts', icon: Shield },
    { key: 'opportunities', label: 'Opportunity Leads', icon: TrendingUp },
    { key: 'archived', label: 'Archived', icon: Archive },
    { key: 'notifications', label: 'Notifications', icon: Bell },
  ];

  const getScoreColor = (score: number | null) => {
    if (score == null) return 'text-slate-400';
    if (score >= 75) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBg = (score: number | null) => {
    if (score == null) return 'bg-slate-700';
    if (score >= 75) return 'bg-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  const getSourceBadge = (source: string | null) => {
    if (!source) return null;
    switch (source) {
      case 'convergence':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300">
            Convergence
          </span>
        );
      case 'analyst':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300">
            Analyst
          </span>
        );
      case 'banker':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300">
            Banker
          </span>
        );
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-300 font-medium">
            HIGH
          </span>
        );
      case 'medium':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-300 font-medium">
            MED
          </span>
        );
      case 'low':
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-500/20 text-slate-300 font-medium">
            LOW
          </span>
        );
      default:
        return null;
    }
  };

  // Score dots (up to 3) as a quick visual confidence indicator
  const renderScoreDots = (score: number) => {
    const filled = score >= 75 ? 3 : score >= 60 ? 2 : 1;
    const color = score >= 75 ? 'bg-emerald-400' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';
    return (
      <div className="flex items-center gap-0.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${i < filled ? color : 'bg-slate-600'}`}
          />
        ))}
      </div>
    );
  };

  // Individual lead row (inside a symbol group)
  const renderLeadRow = (
    lead: BankerLead,
    latestIds: Map<string, number>,
    showIdentity = false
  ) => {
    const isExpanded = expandedLeadId === lead.id;
    const isBuy = lead.direction === 'BUY';
    const hasWatchlistConvergence = convergedLeadIds.has(lead.id);
    const isLatest = latestIds.get(`${extractExchange(lead)}|${lead.symbol ?? ''}`) === lead.id;

    return (
      <div key={lead.id} className="border-t border-slate-700/40 first:border-t-0">
        {/* Row header */}
        <div
          className="px-4 py-3 min-h-[44px] flex flex-col justify-center cursor-pointer hover:bg-slate-700/20 transition-colors"
          onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              {isBuy ? (
                <ArrowUpCircle size={14} className="text-emerald-400 shrink-0" />
              ) : (
                <ArrowDownCircle size={14} className="text-red-400 shrink-0" />
              )}
              {showIdentity && (
                <>
                  <span className="font-semibold text-white">{lead.symbol || 'Unknown symbol'}</span>
                  <span className="text-xs text-slate-500">{extractExchange(lead)}</span>
                  <span className={`text-xs font-bold ${isBuy ? 'text-emerald-400' : 'text-red-400'}`}>
                    {lead.direction}
                  </span>
                </>
              )}
              {getSourceBadge(lead.source)}
              {hasWatchlistConvergence && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300 font-medium">
                  <GitMerge size={10} />
                  Converged
                </span>
              )}
              {lead.strategy_matched && (
                <div className="flex items-center gap-1">
                  <Target size={11} className="text-slate-500" />
                  <span className="text-xs text-slate-400">
                    {lead.strategy_matched.replace(/_/g, ' ')}
                  </span>
                </div>
              )}
              {lead.status === 'acted_on' && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300 font-medium">
                  Acted On
                </span>
              )}
              {lead.rejection_reason && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-300">
                  {lead.rejection_reason.replace(/_/g, ' ')}
                </span>
              )}
              {lead.advice_draft_id && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-300 font-medium" title="Sourced from validated research artefact">
                  <Shield size={10} />
                  Research
                </span>
              )}
              <LatestBadge latest={isLatest} />
            </div>

            <div className="flex items-center gap-3">
              <div className={`px-2.5 py-0.5 rounded-md ${getScoreBg(lead.gate_score)}`}>
                <span className={`text-xs font-bold ${getScoreColor(lead.gate_score)}`}>
                  {lead.gate_score != null ? `${Math.round(lead.gate_score)}%` : '--'}
                </span>
              </div>
              {lead.entry_price && (
                <div className="text-xs text-slate-500 hidden md:flex gap-3">
                  <span>E: {lead.entry_price}</span>
                  {lead.stop_loss && <span>SL: {lead.stop_loss}</span>}
                  {lead.take_profit && <span>TP: {lead.take_profit}</span>}
                </div>
              )}
              {lead.risk_reward_ratio && (
                <div className="flex items-center gap-1">
                  <TrendingUp size={12} className="text-slate-500" />
                  <span className="text-xs text-slate-400">1:{lead.risk_reward_ratio}</span>
                </div>
              )}
              {isExpanded ? (
                <ChevronDown size={14} className="text-slate-500" />
              ) : (
                <ChevronRight size={14} className="text-slate-500" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
            <AnalysisTimestamp date={lead.created_at} />
            <span>Entry / stop / target as of analysis time</span>
          </div>
        </div>

        {/* Expanded lead details */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 bg-slate-800/30">
            {lead.chart_image && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Price Chart (90d)
                </h4>
                <div className="h-48 lg:h-64 w-full">
                  <img
                    src={`data:image/png;base64,${lead.chart_image}`}
                    alt={`${lead.symbol} candlestick chart`}
                    className="w-full h-full object-contain rounded-lg border border-slate-700/50"
                  />
                </div>
              </div>
            )}
            {lead.ta_signals && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                  TradingView Signals
                </h4>
                <div className="flex flex-wrap items-start gap-3">
                  {/* Recommendation pill */}
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${
                    lead.ta_signals.recommendation?.includes('BUY')
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : lead.ta_signals.recommendation?.includes('SELL')
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-slate-600/40 text-slate-400 border border-slate-600/30'
                  }`}>
                    {lead.ta_signals.recommendation?.replace(/_/g, ' ') ?? 'NEUTRAL'}
                  </div>
                  {/* Vote counts */}
                  <div className="flex gap-2 text-xs text-slate-400 items-center">
                    <span className="text-emerald-400">{lead.ta_signals.buy_count}↑</span>
                    <span className="text-red-400">{lead.ta_signals.sell_count}↓</span>
                    <span className="text-slate-500">{lead.ta_signals.neutral_count}–</span>
                  </div>
                  {/* Key indicators */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                    {lead.ta_signals.indicators?.RSI != null && (
                      <span>RSI <span className="text-slate-200">{lead.ta_signals.indicators.RSI.toFixed(1)}</span></span>
                    )}
                    {lead.ta_signals.indicators?.['MACD.macd'] != null && (
                      <span>MACD <span className="text-slate-200">{lead.ta_signals.indicators['MACD.macd'].toFixed(2)}</span></span>
                    )}
                    {lead.ta_signals.indicators?.ADX != null && (
                      <span>ADX <span className="text-slate-200">{lead.ta_signals.indicators.ADX.toFixed(1)}</span></span>
                    )}
                    {lead.ta_signals.indicators?.volume != null && (
                      <span>Vol <span className="text-slate-200">{lead.ta_signals.indicators.volume.toLocaleString('en-US')}</span></span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {lead.technical_rationale && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                  Technical Rationale
                </h4>
                <p className="text-sm text-slate-300">{lead.technical_rationale}</p>
              </div>
            )}
            {lead.thesis && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Thesis</h4>
                <p className="text-sm text-slate-300">{lead.thesis}</p>
              </div>
            )}
            {lead.catalyst && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Catalyst</h4>
                <p className="text-sm text-slate-300">{lead.catalyst}</p>
              </div>
            )}
            {lead.contra_summary && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Shield size={12} /> Contra Assessment
                </h4>
                <p className="text-sm text-slate-300">{lead.contra_summary}</p>
              </div>
            )}
            {lead.portfolio_fit && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                  Portfolio Fit
                </h4>
                <p className="text-sm text-slate-300">{lead.portfolio_fit}</p>
              </div>
            )}

            {lead.entry_price && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-slate-700/30">
                <div>
                  <span className="text-xs text-slate-500">Entry</span>
                  <p className="text-sm font-medium text-white">{lead.entry_price}</p>
                </div>
                {lead.stop_loss && (
                  <div>
                    <span className="text-xs text-slate-500">Stop Loss</span>
                    <p className="text-sm font-medium text-red-400">{lead.stop_loss}</p>
                  </div>
                )}
                {lead.take_profit && (
                  <div>
                    <span className="text-xs text-slate-500">Take Profit</span>
                    <p className="text-sm font-medium text-emerald-400">{lead.take_profit}</p>
                  </div>
                )}
                {lead.position_size && (
                  <div>
                    <span className="text-xs text-slate-500">Position Size</span>
                    <p className="text-sm font-medium text-white">{lead.position_size}</p>
                  </div>
                )}
                {lead.risk_reward_ratio && (
                  <div>
                    <span className="text-xs text-slate-500">R:R</span>
                    <p className="text-sm font-medium text-white">1:{lead.risk_reward_ratio}</p>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500">Entry / stop / target as of analysis time</p>

            {(lead.status === 'approved' || lead.status === 'acted_on') && canActOnSignals && (
              <div className="pt-2 border-t border-slate-700/30">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markActed.mutate(lead.id);
                  }}
                  disabled={markActed.isPending}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    lead.status === 'acted_on'
                      ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {lead.status === 'acted_on' ? 'Marked as Acted On' : 'Mark as Acted On'}
                </button>
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-slate-500 pt-2">
              {lead.exchange && <span>Exchange: {lead.exchange}</span>}
              {lead.created_at && (
                <span title={formatAbsoluteDateTime(lead.created_at)}>
                  Analysed {formatRelativeTime(lead.created_at)}
                </span>
              )}
              {lead.archived_at && (
                <span title={formatAbsoluteDateTime(lead.archived_at)}>
                  Archived {formatRelativeTime(lead.archived_at)}
                </span>
              )}
            </div>

            {/* Outcome pill (v32-1-1) */}
            {lead.outcome_direction && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700/50">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  lead.outcome_direction === 'win'        ? 'bg-green-900/60 text-green-300' :
                  lead.outcome_direction === 'loss'       ? 'bg-red-900/60 text-red-300' :
                                                            'bg-slate-700 text-slate-400'
                }`}>
                  {lead.outcome_direction.toUpperCase()}
                </span>
                {lead.outcome_pct != null && (
                  <span className={`text-sm font-mono ${lead.outcome_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {lead.outcome_pct >= 0 ? '+' : ''}{lead.outcome_pct.toFixed(2)}%
                  </span>
                )}
                {lead.outcome_closed_at && (
                  <span className="text-xs text-slate-500" title={formatAbsoluteDateTime(lead.outcome_closed_at)}>
                    closed {formatRelativeTime(lead.outcome_closed_at)}
                  </span>
                )}
                {lead.outcome_note && (
                  <span className="text-xs text-slate-400 italic">{lead.outcome_note}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Symbol group card (inside an exchange section)
  const renderSymbolGroup = (group: SymbolGroup, exchangeKey: string, latestIds: Map<string, number>) => {
    const symbolKey = `${exchangeKey}::${group.symbol}`;
    const isExpanded = expandedSymbol === symbolKey;
    const isBuy = group.direction === 'BUY';
    const confluenceCount = group.leads.length;

    return (
      <div
        key={symbolKey}
        className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden"
      >
        {/* Symbol group header */}
        <div
          className="px-4 py-3 min-h-[44px] flex flex-col justify-center cursor-pointer hover:bg-slate-700/30 transition-colors"
          onClick={() => {
            const next = isExpanded ? null : symbolKey;
            setExpandedSymbol(next);
            if (!isExpanded) {
              setExpandedLeadId(null);
              trackEvent('company_click', group.symbol, { source_page: 'banker_leads' });
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isBuy ? (
                <ArrowUpCircle size={18} className="text-emerald-400 shrink-0" />
              ) : (
                <ArrowDownCircle size={18} className="text-red-400 shrink-0" />
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white">{group.symbol}</span>
                <span
                  className={`text-xs font-bold ${isBuy ? 'text-emerald-400' : 'text-red-400'}`}
                >
                  {group.direction}
                </span>
                {renderScoreDots(group.bestScore)}
                <span className="text-xs text-slate-500">
                  {confluenceCount} confluence{confluenceCount !== 1 ? 's' : ''}
                </span>
                {group.leads[0]?.created_at && (
                  <span className="text-xs text-slate-500" title={formatAbsoluteDateTime(group.leads[0].created_at)}>
                    latest {formatRelativeTime(group.leads[0].created_at)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 rounded-lg ${getScoreBg(group.bestScore)}`}>
                <span className={`text-sm font-bold ${getScoreColor(group.bestScore)}`}>
                  {Math.round(group.bestScore)}%
                </span>
              </div>
              {isExpanded ? (
                <ChevronDown size={15} className="text-slate-400" />
              ) : (
                <ChevronRight size={15} className="text-slate-400" />
              )}
            </div>
          </div>
        </div>

        {/* Individual leads within this symbol */}
        {isExpanded && (
          <div className="border-t border-slate-700/40">
            {group.leads.map((lead) => renderLeadRow(lead, latestIds))}
          </div>
        )}
      </div>
    );
  };

  // Exchange section (top-level accordion)
  const renderExchangeSection = (group: ExchangeGroup, latestIds: Map<string, number>) => {
    const isExpanded = expandedExchange === group.exchange;
    const symbolCount = group.symbols.length;
    const totalLeads = group.symbols.reduce((n, s) => n + s.leads.length, 0);

    return (
      <div key={group.exchange} className="border border-slate-700/50 rounded-xl overflow-hidden">
        {/* Exchange header */}
        <div
          className="flex items-center justify-between px-5 py-3.5 min-h-[44px] bg-slate-800 cursor-pointer hover:bg-slate-700/50 transition-colors"
          onClick={() => {
            const next = isExpanded ? null : group.exchange;
            setExpandedExchange(next);
            if (!isExpanded) {
              setExpandedSymbol(null);
              setExpandedLeadId(null);
            }
          }}
        >
          <div className="flex items-center gap-3">
            <Globe size={16} className="text-teal-400 shrink-0" />
            <span className="font-semibold text-white text-sm tracking-wide">
              {group.exchange}
            </span>
            <span className="text-xs text-slate-400">
              {symbolCount} symbol{symbolCount !== 1 ? 's' : ''} · {totalLeads} lead
              {totalLeads !== 1 ? 's' : ''}
            </span>
            {group.symbols[0]?.leads[0]?.created_at && (
              <span
                className="text-xs text-slate-500"
                title={formatAbsoluteDateTime(group.symbols[0].leads[0].created_at)}
              >
                latest {formatRelativeTime(group.symbols[0].leads[0].created_at)}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronDown size={16} className="text-slate-400" />
          ) : (
            <ChevronRight size={16} className="text-slate-400" />
          )}
        </div>

        {/* Symbol groups within exchange */}
        {isExpanded && (
          <div className="p-3 space-y-2 bg-slate-900/30">
            {group.symbols.map((sg) => renderSymbolGroup(sg, group.exchange, latestIds))}
          </div>
        )}
      </div>
    );
  };

  const getEAStatusColor = (status: string | undefined) => {
    if (!status) return 'text-slate-500';
    switch (status) {
      case 'delivered': return 'text-emerald-400';
      case 'pending': return 'text-amber-400';
      case 'failed': return 'text-red-400';
      default: return 'text-slate-500';
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const renderNotificationRow = (item: NotificationHistoryItem) => {
    const isExpanded = expandedLeadId === item.notification_id;
    const isBuy = item.direction === 'BUY';
    const isSell = item.direction === 'SELL';

    return (
      <div
        key={item.notification_id}
        className="bg-slate-800 border border-slate-700/50 rounded-lg overflow-hidden"
      >
        <div
          className="p-4 min-h-[44px] flex flex-col justify-center cursor-pointer hover:bg-slate-700/30 transition-colors"
          onClick={() => setExpandedLeadId(isExpanded ? null : item.notification_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isBuy ? (
                <ArrowUpCircle size={20} className="text-emerald-400" />
              ) : isSell ? (
                <ArrowDownCircle size={20} className="text-red-400" />
              ) : (
                <Bell size={20} className="text-amber-400" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {formatTime(item.notification_created_at)}
                  </span>
                  <span className="font-semibold text-white">{item.symbol}</span>
                  {item.direction && (
                    <span
                      className={`text-xs font-bold ${
                        isBuy ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {item.direction}
                    </span>
                  )}
                  {getPriorityBadge(item.notification_priority)}
                  <span className="text-xs text-slate-400">{item.persona_name}</span>
                </div>
                {item.strategy_matched && (
                  <div className="flex items-center gap-1 mt-1">
                    <Target size={12} className="text-slate-400" />
                    <span className="text-xs text-slate-400">
                      {item.strategy_matched.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5" title="In-App: delivered">
                  <Bell size={14} className="text-emerald-400" />
                </div>
                <div
                  className="flex items-center gap-0.5"
                  title={`Chat: ${item.has_chat_message ? 'delivered' : 'no message'}`}
                >
                  <MessageSquare
                    size={14}
                    className={item.has_chat_message ? 'text-emerald-400' : 'text-slate-500'}
                  />
                </div>
                <div
                  className="flex items-center gap-0.5"
                  title={item.ea_status ? `EA: ${item.ea_status.status}` : 'EA: n/a'}
                >
                  <Smartphone size={14} className={getEAStatusColor(item.ea_status?.status)} />
                </div>
              </div>

              <div className={`px-3 py-1 rounded-lg ${getScoreBg(item.gate_score)}`}>
                <span className={`text-sm font-bold ${getScoreColor(item.gate_score)}`}>
                  {item.gate_score != null ? `${Math.round(item.gate_score)}%` : '--'}
                </span>
              </div>

              {isExpanded ? (
                <ChevronDown size={16} className="text-slate-400" />
              ) : (
                <ChevronRight size={16} className="text-slate-400" />
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-slate-700/50 p-4 space-y-3 bg-slate-800/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-3 border-b border-slate-700/30">
              <div>
                <span className="text-xs text-slate-500">Notification Sent</span>
                <p className="text-sm text-slate-300">
                  {new Date(item.notification_created_at).toLocaleString()}
                </p>
              </div>
              {item.ea_status?.delivered_at && (
                <div>
                  <span className="text-xs text-slate-500">EA Delivered</span>
                  <p className="text-sm text-slate-300">
                    {new Date(item.ea_status.delivered_at).toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <span className="text-xs text-slate-500">Status</span>
                <p className="text-sm text-slate-300">
                  {item.notification_is_read ? 'Read' : 'Unread'}
                </p>
              </div>
            </div>

            {item.technical_rationale && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                  Technical Rationale
                </h4>
                <p className="text-sm text-slate-300">{item.technical_rationale}</p>
              </div>
            )}
            {item.thesis && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Thesis</h4>
                <p className="text-sm text-slate-300">{item.thesis}</p>
              </div>
            )}
            {item.catalyst && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Catalyst</h4>
                <p className="text-sm text-slate-300">{item.catalyst}</p>
              </div>
            )}
            {item.contra_summary && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Shield size={12} /> Contra Assessment
                </h4>
                <p className="text-sm text-slate-300">{item.contra_summary}</p>
              </div>
            )}
            {item.portfolio_fit && (
              <div>
                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                  Portfolio Fit
                </h4>
                <p className="text-sm text-slate-300">{item.portfolio_fit}</p>
              </div>
            )}

            {item.entry_price && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-slate-700/30">
                <div>
                  <span className="text-xs text-slate-500">Entry</span>
                  <p className="text-sm font-medium text-white">{item.entry_price}</p>
                </div>
                {item.stop_loss && (
                  <div>
                    <span className="text-xs text-slate-500">Stop Loss</span>
                    <p className="text-sm font-medium text-red-400">{item.stop_loss}</p>
                  </div>
                )}
                {item.take_profit && (
                  <div>
                    <span className="text-xs text-slate-500">Take Profit</span>
                    <p className="text-sm font-medium text-emerald-400">{item.take_profit}</p>
                  </div>
                )}
                {item.position_size && (
                  <div>
                    <span className="text-xs text-slate-500">Position Size</span>
                    <p className="text-sm font-medium text-white">{item.position_size}</p>
                  </div>
                )}
                {item.risk_reward_ratio && (
                  <div>
                    <span className="text-xs text-slate-500">R:R</span>
                    <p className="text-sm font-medium text-white">1:{item.risk_reward_ratio}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-slate-500 pt-2">
              {item.exchange && <span>Exchange: {item.exchange}</span>}
              {getSourceBadge(item.source)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Group notifications by date for section headers
  const groupedNotifications = notifications.reduce<Record<string, NotificationHistoryItem[]>>(
    (groups, item) => {
      const key = formatDate(item.notification_created_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
      return groups;
    },
    {}
  );

  const isNotifTab = activeTab === 'notifications';
  const isAlertTab = activeTab === 'position_alerts';
  const isOppTab = activeTab === 'opportunities';
  const isArchivedTab = activeTab === 'archived';

  const currentLoading = isNotifTab
    ? notifLoading
    : isAlertTab
    ? alertLoading
    : isOppTab
    ? oppLoading
    : archivedOppLoading || archivedAlertLoading;
  const currentError = isNotifTab
    ? notifError
    : isAlertTab
    ? alertError
    : isOppTab
    ? oppError
    : archivedOppError || archivedAlertError;

  const oppExchangeGroups = groupLeadsByExchangeThenSymbol(oppLeads);
  const alertExchangeGroups = groupLeadsByExchangeThenSymbol(alertLeads);
  const archivedLeads = [...archivedAlertLeads, ...archivedOppLeads];
  const archivedExchangeGroups = groupLeadsByExchangeThenSymbol(archivedLeads);
  const alertLatestIds = latestLeadIdBySymbol(alertLeads);
  const oppLatestIds = latestLeadIdBySymbol(oppLeads);
  const archivedLatestIds = latestLeadIdBySymbol(archivedLeads);
  const visibleAlertLeads = leadView === 'latest' ? sortLeadsNewestFirst(alertLeads) : alertLeads;

  return (
    <PermissionGate permission="feature:banker" fallback={<div className="p-8"><UpgradeNudge tier="premium" /></div>}>
    <div className="p-4 lg:p-6 max-w-6xl mx-auto pb-20 lg:pb-0">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Banker Leads</h1>
        <p className="text-slate-400 mt-1">
          Position alerts from your holdings monitor and opportunity leads from your Investment Banker
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-slate-800 rounded-lg p-1 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const count =
            tab.key === 'notifications'
              ? notifData?.total_7d
              : tab.key === 'position_alerts'
              ? alertData?.total
              : tab.key === 'opportunities'
              ? oppData?.total
              : tab.key === 'archived'
              ? (archivedOppData?.total ?? 0) + (archivedAlertData?.total ?? 0) || undefined
              : undefined;
          return (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setExpandedExchange(null);
                setExpandedSymbol(null);
                setExpandedLeadId(null);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-slate-700 text-teal-400'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
              {count !== undefined && (
                <span
                  className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    tab.key === 'notifications' ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Notification filters */}
      {isNotifTab && (
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex bg-slate-800 rounded-lg p-0.5">
            {(['today', '7d', '30d', 'all'] as DateRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  dateRange === range
                    ? 'bg-slate-700 text-teal-400'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {range === 'today'
                  ? 'Today'
                  : range === '7d'
                  ? '7 days'
                  : range === '30d'
                  ? '30 days'
                  : 'All'}
              </button>
            ))}
          </div>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
            className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      )}

      {(isAlertTab || isArchivedTab) && (
        <div className="flex justify-end mb-4">
          {renderLeadViewToggle()}
        </div>
      )}

      {/* Loading / error */}
      {currentLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
        </div>
      )}

      {currentError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Failed to load {isNotifTab ? 'notifications' : 'leads'}:{' '}
          {(currentError as Error).message}
        </div>
      )}

      {/* Position Alerts — flat list (Pass 1 holdings monitor) */}
      {isAlertTab && !alertLoading && !alertError && alertLeads.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Shield size={32} className="mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No position alerts</p>
          <p className="text-sm mt-1">
            When the Banker monitors your holdings and detects P&L or risk thresholds, alerts will appear here.
          </p>
        </div>
      )}

      {isAlertTab && !alertLoading && !alertError && alertLeads.length > 0 && (
        <div className="space-y-2">
          {leadView === 'exchange' ? alertExchangeGroups.map((group) => renderExchangeSection(group, alertLatestIds)) : visibleAlertLeads.map((lead) => {
            const isExpanded = expandedLeadId === lead.id;
            const isBuy = lead.direction === 'BUY';
            const isLatest = alertLatestIds.get(`${extractExchange(lead)}|${lead.symbol ?? ''}`) === lead.id;
            return (
              <div key={lead.id} className="bg-slate-800 border border-slate-700/50 rounded-xl overflow-hidden">
                <div
                  className="px-5 py-3.5 min-h-[44px] flex flex-col justify-center cursor-pointer hover:bg-slate-700/30 transition-colors"
                  onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isBuy ? (
                        <ArrowUpCircle size={18} className="text-emerald-400 shrink-0" />
                      ) : (
                        <ArrowDownCircle size={18} className="text-red-400 shrink-0" />
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white">{lead.symbol}</span>
                        <span className={`text-xs font-bold ${isBuy ? 'text-emerald-400' : 'text-red-400'}`}>
                          {lead.direction}
                        </span>
                        {lead.status === 'acted_on' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-teal-500/20 text-teal-300 font-medium">
                            Acted On
                          </span>
                        )}
                        <LatestBadge latest={isLatest} />
                      </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 ml-7">
                    <AnalysisTimestamp date={lead.created_at} />
                    <span>Entry / stop / target as of analysis time</span>
                  </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {lead.gate_score != null && (
                        <div className={`px-2.5 py-0.5 rounded-md ${getScoreBg(lead.gate_score)}`}>
                          <span className={`text-xs font-bold ${getScoreColor(lead.gate_score)}`}>
                            {Math.round(lead.gate_score)}%
                          </span>
                        </div>
                      )}
                      {lead.created_at && (
                        <span className="text-xs text-slate-500 hidden md:inline" title={formatAbsoluteDateTime(lead.created_at)}>
                          {formatRelativeTime(lead.created_at)}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronDown size={15} className="text-slate-400" />
                      ) : (
                        <ChevronRight size={15} className="text-slate-400" />
                      )}
                    </div>
                  </div>
                  {lead.technical_rationale && !isExpanded && (
                    <p className="text-xs text-slate-400 mt-1.5 ml-7 line-clamp-2">
                      {lead.technical_rationale}
                    </p>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-slate-700/40 px-5 pb-4 pt-3 space-y-3 bg-slate-800/30">
                    {lead.technical_rationale && (
                      <div>
                        <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Alert Detail</h4>
                        <p className="text-sm text-slate-300">{lead.technical_rationale}</p>
                      </div>
                    )}
                    {lead.thesis && (
                      <div>
                        <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-1">Thesis</h4>
                        <p className="text-sm text-slate-300">{lead.thesis}</p>
                      </div>
                    )}
                    {lead.entry_price && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-slate-700/30">
                        <div>
                          <span className="text-xs text-slate-500">Entry</span>
                          <p className="text-sm font-medium text-white">{lead.entry_price}</p>
                        </div>
                        {lead.stop_loss && (
                          <div>
                            <span className="text-xs text-slate-500">Stop Loss</span>
                            <p className="text-sm font-medium text-red-400">{lead.stop_loss}</p>
                          </div>
                        )}
                        {lead.take_profit && (
                          <div>
                            <span className="text-xs text-slate-500">Take Profit</span>
                            <p className="text-sm font-medium text-emerald-400">{lead.take_profit}</p>
                          </div>
                        )}
                        {lead.risk_reward_ratio && (
                          <div>
                            <span className="text-xs text-slate-500">R:R</span>
                            <p className="text-sm font-medium text-white">1:{lead.risk_reward_ratio}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">Entry / stop / target as of analysis time</p>
                    {(lead.status === 'approved' || lead.status === 'acted_on') && canActOnSignals && (
                      <div className="pt-2 border-t border-slate-700/30">
                        <button
                          onClick={(e) => { e.stopPropagation(); markActed.mutate(lead.id); }}
                          disabled={markActed.isPending}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            lead.status === 'acted_on'
                              ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {lead.status === 'acted_on' ? 'Marked as Acted On' : 'Mark as Acted On'}
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
                      {lead.exchange && <span>Exchange: {lead.exchange}</span>}
                      {lead.created_at && (
                        <span title={formatAbsoluteDateTime(lead.created_at)}>
                          Analysed {formatRelativeTime(lead.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Opportunity Leads — exchange → symbol accordion (Pass 2) */}
      {isOppTab && (
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => { setShowRejected(false); setExpandedExchange(null); setExpandedSymbol(null); setExpandedLeadId(null); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showRejected ? 'bg-slate-700 text-teal-400' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Approved
          </button>
          <button
            onClick={() => { setShowRejected(true); setExpandedExchange(null); setExpandedSymbol(null); setExpandedLeadId(null); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showRejected ? 'bg-slate-700 text-amber-400' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Rejected
          </button>
          <div className="ml-auto">{renderLeadViewToggle()}</div>
        </div>
      )}

      {isOppTab && !oppLoading && !oppError && oppLeads.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <TrendingUp size={32} className="mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No {showRejected ? 'rejected' : 'approved'} opportunity leads</p>
          <p className="text-sm mt-1">
            {showRejected
              ? 'Leads that did not pass the validation gate appear here for transparency.'
              : 'When the Banker identifies and validates new opportunities, approved leads will appear here.'}
          </p>
        </div>
      )}

      {isOppTab && !oppLoading && !oppError && oppLeads.length > 0 && (
        <div className="space-y-3">
          {leadView === 'exchange'
            ? oppExchangeGroups.map((group) => renderExchangeSection(group, oppLatestIds))
            : sortLeadsNewestFirst(oppLeads).map((lead) => renderLeadRow(lead, oppLatestIds, true))}
        </div>
      )}

      {/* Archived leads — newest-first by default, optionally grouped by exchange and symbol */}
      {isArchivedTab && !archivedOppLoading && !archivedAlertLoading && !archivedOppError && !archivedAlertError
        && archivedOppLeads.length === 0 && archivedAlertLeads.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Archive size={32} className="mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No archived leads</p>
          <p className="text-sm mt-1">
            Old leads are automatically archived based on your retention settings.
          </p>
        </div>
      )}

      {isArchivedTab && (!archivedOppLoading && !archivedAlertLoading) && (archivedOppLeads.length > 0 || archivedAlertLeads.length > 0) && (
        <div className="space-y-3">
          {leadView === 'exchange'
            ? archivedExchangeGroups.map((group) => renderExchangeSection(group, archivedLatestIds))
            : sortLeadsNewestFirst(archivedLeads).map((lead) => renderLeadRow(lead, archivedLatestIds, true))}
        </div>
      )}

      {/* Notifications content — grouped by date */}
      {isNotifTab && !notifLoading && !notifError && notifications.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Bell size={32} className="mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No notifications found</p>
          <p className="text-sm mt-1">
            When the Banker sends you alerts about approved opportunities, they will appear here.
          </p>
        </div>
      )}

      {isNotifTab && !notifLoading && !notifError && notifications.length > 0 && (
        <div className="space-y-4">
          {Object.entries(groupedNotifications).map(([dateLabel, items]) => (
            <div key={dateLabel}>
              <h3 className="text-xs text-slate-500 uppercase tracking-wide mb-2 px-1">
                {dateLabel}
              </h3>
              <div className="space-y-2">{items.map(renderNotificationRow)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    </PermissionGate>
  );
};

export default BankerLeads;
